import { DatePipe, DecimalPipe, LowerCasePipe, NgClass, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import {
  Component,
  effect,
  inject,
  input,
  InputSignal,
  OnInit,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import {
  MatCell,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderRow,
  MatNoDataRow,
  MatRow,
  MatTable,
  MatTableDataSource,
  MatTableModule
} from '@angular/material/table';
import { MatRipple } from '@angular/material/core';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { AmlScanEntity, IngoingTransaction, LatestAmlReport, PaginationResponse, TableSetting, TableSettingRequest } from "../../../../../../api/api.model";
import { MatMenu, MatMenuTrigger } from "@angular/material/menu";
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from "@angular/cdk/drag-drop";
import { CdkScrollable } from "@angular/cdk/scrolling";
import { MatCheckbox, MatCheckboxChange } from "@angular/material/checkbox";
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from "@angular/forms";
import { ApiService } from "../../../../../../api/api.service";
import { filter, Subscription, take } from "rxjs";
import { AuthService } from "../../../../../../partials/services/auth.service";
import { SidenavService } from '../../../../../../partials/services/sidenav.service';
import { ReportsComponent } from '../../../../../../partials/components/reports/reports.component';
import { TransformUnderscoreToSpacePipe } from '../../../../../../partials/pipes/tranform-underscore-to-space.pipe';
import { MatSelectModule } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CropWithDotsCenterPipe } from '../../../../../../partials/pipes/crop-with-dots-center.pipe';
import { CopyButtonComponent } from '../../../../../../partials/components/copy-button/copy-button.component';
import { PopoverDirective } from '../../../../../../partials/module/popover/popover.directive';
import { PopoverContainerComponent } from '../../../../../../partials/module/popover/popover-container/popover-container.component';
import { ReportStatusComponent } from '../../../../../../partials/components/report-status/report-status.component';
import { TriggerIdConvertToCategoryPipe } from '../../../../../../partials/pipes/trigger-id-convert-to-category.pipe';
import { EmptyTableComponent } from '../../../../../../partials/components/empty-table/empty-table.component';
import { EmptyPaginationComponent } from '../../../../../../partials/components/empty-pagination/empty-pagination.component';

// Настройки таблицы которые сохраняются в API
export interface MonitoringTableSettings {
  allDisplayedColumns: string[];
  filteredDisplayedColumns: string[];
}

// Имя таблицы необходимо для сохранения настроек в API
export const tableName = 'TransactionsTable';

@Component({
  selector: 'app-monitoring-table',
  imports: [
    MatIconModule,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderRow,
    MatRow,
    MatFormFieldModule,
    MatRipple,
    MatPaginatorModule,
    MatNoDataRow,
    DatePipe,
    DecimalPipe,
    MatMenuTrigger,
    MatMenu,
    CdkDropList,
    CdkDrag,
    CdkScrollable,
    CdkDragHandle,
    MatCheckbox,
    FormsModule,
    MatTableModule,
    NgClass,
    TransformUnderscoreToSpacePipe,
    TitleCasePipe,
    MatSelectModule,
    ReactiveFormsModule,
    LowerCasePipe,
    CropWithDotsCenterPipe,
    CopyButtonComponent,
    PopoverDirective,
    PopoverContainerComponent,
    ReportStatusComponent,
    TriggerIdConvertToCategoryPipe,
    NgTemplateOutlet,
    EmptyTableComponent,
    EmptyPaginationComponent
],
  templateUrl: './monitoring-table.component.html',
  styleUrl: './monitoring-table.component.scss'
})
export class MonitoringTableComponent implements OnInit {
  private readonly _apiService: ApiService = inject(ApiService);

  private _mainSidenavService: SidenavService = inject(SidenavService);

  private readonly _authService: AuthService = inject(AuthService);

  readonly pageEvent: OutputEmitterRef<PageEvent> = output<PageEvent>();

  readonly outputEventUpdateStatuses: OutputEmitterRef<void> = output<void>();
  readonly outputFilterForm: OutputEmitterRef<{
    amlScanType?: string,
    sourceType?: string,
    nickname?: string
  }> = output<{ amlScanType?: string, sourceType?: string, nickname?: string }>({});

  readonly paginationResponseData: InputSignal<PaginationResponse<AmlScanEntity>> = input.required<PaginationResponse<AmlScanEntity>>();

  readonly isGrouped: InputSignal<boolean> = input.required<boolean>();

  readonly paginationOptions: InputSignal<{
    pageSize: number;
    pageIndex: number;
    pageSizeOptions: number[];
    length: number
  }> = input.required<{ pageSize: number; pageIndex: number; pageSizeOptions: number[]; length: number }>();

  public markOpenRow: WritableSignal<string> = signal('');
  public nicknamesList: WritableSignal<string[]> = signal([]);
  public sortingList: WritableSignal<{ amlScanTypes: string[], sourceTypes: string[] }> = signal({
    amlScanTypes: [],
    sourceTypes: []
  });

  private readonly _fb = inject(NonNullableFormBuilder);

  public filterForm: FormGroup = this._fb.group({
    nickname: this._fb.control<string | null>(null),
    amlScanType: this._fb.control<string | null>(null),
    sourceType: this._fb.control<string | null>(null),
  });

  readonly tableSettings: InputSignal<TableSetting<MonitoringTableSettings> | undefined> = input<TableSetting<MonitoringTableSettings> | undefined>();

  allDisplayedColumns: string[] = [
    'date',
    'source',
    'risk_score',
    'deposit_wallet',
    'monitoring_status',
    // 'category',
    // 'nickname',
    'value',
    // 'coin',
    'network',
    'risky_amount',
    'report_type',
    // 'exposure',
    'service_fee',
    'blockchain_fee',
    'action',
    'table_settings'
  ];

  private _viewSubscription!: Subscription;

  filteredDisplayedColumns: string[] = [];

  get displayedColumns() {
    return this.allDisplayedColumns.filter((column) => !this.filteredDisplayedColumns.includes(column));
  };

  get displayedColumnsEmpty() {
    const displayedColumns = this.displayedColumns;
    return displayedColumns.filter(item => item !== 'table_settings');
  };

  readonly dataSource: MatTableDataSource<AmlScanEntity> = new MatTableDataSource<AmlScanEntity>([]);

  constructor() {
    this.filterForm.valueChanges.pipe(takeUntilDestroyed()).subscribe({
      next: (value) => {
        this.outputFilterForm.emit(value);
      }
    });
    effect(() => {
      const { content } = this.paginationResponseData();
      this.dataSource.data = content;
    });

    effect(() => {
      const loadedTableSettings = this.tableSettings();

      if (loadedTableSettings) {
        const { data: { allDisplayedColumns, filteredDisplayedColumns } } = loadedTableSettings;
        // Проверка нужно для того чтоб если были изменения в allDisplayedColumns.
        // Мы не перезаписывали сохраненными настройками.
        if (
          this.allDisplayedColumns.every((displayedColumn) => allDisplayedColumns.includes(displayedColumn))
          &&
          allDisplayedColumns.every((displayedColumn) => this.allDisplayedColumns.includes(displayedColumn))
        ) {
          this.allDisplayedColumns = allDisplayedColumns;
          this.filteredDisplayedColumns = filteredDisplayedColumns;
        }
      }
    });
  }

  ngOnInit(): void {
    this._apiService.getWalletNicknames().pipe(take(1)).subscribe({
      next: (value: string[]) => {
        this.nicknamesList.set(value);
      }
    });
    this._apiService.getSortingOptionsMonitoring().pipe(take(1)).subscribe({
      next: (value: { amlScanTypes: string[], sourceTypes: string[] }) => {
        this.sortingList.set(value);
      }
    });
  }

  private _saveTableSettings(): void {
    const user = this._authService.accountDto();

    if (!user) return;

    const { uuid } = user;

    const body: TableSettingRequest = {
      data: {
        allDisplayedColumns: this.allDisplayedColumns,
        filteredDisplayedColumns: this.filteredDisplayedColumns,
      },
      tableName,
      userId: uuid
    };
    this._apiService.createOrUpdateTableSetting<MonitoringTableSettings>(body).pipe(take(1)).subscribe({
      next: () => {
        console.log(`${ tableName } settings saved!`);
      }
    });
  }

  handlerChangeDisplayColumnCheckbox({ checked }: MatCheckboxChange, displayedColumn: string): void {
    if (checked) {
      this.filteredDisplayedColumns = this.filteredDisplayedColumns.filter((c) => c !== displayedColumn);
    } else {
      this.filteredDisplayedColumns.push(displayedColumn);
    }
    this._saveTableSettings();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.allDisplayedColumns, event.previousIndex, event.currentIndex);
    this._saveTableSettings();
  }

  viewReport(uuid: string): void {
    this._viewSubscription?.unsubscribe();
    this._viewSubscription = this._apiService.getAmlScanByUuid(uuid).pipe(
      filter(Boolean)
    ).subscribe({
      next: (transactionEntity: AmlScanEntity) => {
        const transaction = {
          ...transactionEntity,
          latestAmlReport: JSON.parse(transactionEntity.latestAmlReport)
        }
        this.outputEventUpdateStatuses.emit();
        this.markOpenRow.set(transaction.uuid);
        let inputs = {
          transaction
        };
        this.pageEvent.emit(this.paginationOptions());
        this._mainSidenavService.open<boolean>({
          template: ReportsComponent,
          inputs
        }).afterClosed.pipe(take(1)).subscribe({
          next: () => {
            this.markOpenRow.set('');
            this.outputEventUpdateStatuses.emit();
            this.pageEvent.emit(this.paginationOptions());
          }
        });
      }
    });
  }

  public asIngoingTransaction(transaction: AmlScanEntity<LatestAmlReport>): AmlScanEntity<IngoingTransaction> {
    return transaction as AmlScanEntity<IngoingTransaction>;
  }
}
